import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchApiData } from "./commonApiSlice";
import { fetchTickets, fetchTicketStats } from "../../data/APIs";
import { fetchTicketSystemIssueType } from "../ticket-system/ticketSystemAPIs";

const initialState = {
  ticketsLoading: false,
  ticketsError: null,
  ticketsData: [],
  ticketsPagination: [],
  ticketStats: null,
  ticketStatsLoading: false,
  ticketStatsError: null,
  // Add supervisor state
  supervisors: [],
  supervisorsLoading: false,
  supervisorsError: null,
  // Analytics data
  analyticsLoading: {
    gymwise: false,
    category: false,
    infra: false,
    tatTrend: false,
    supervisorPerformance: false,
    paidResolutionTracker: false,
    priorityHeatmap: false,
  },
  analyticsError: {
    gymwise: null,
    category: null,
    infra: null,
    tatTrend: null,
    supervisorPerformance: null,
    paidResolutionTracker: null,
    priorityHeatmap: null,
  },
  analyticsData: {
    gymwiseStats: [],
    categoryDistribution: {},
    infraSpend: {},
    tatTrend: [],
    supervisorPerformance: [],
    paidResolutionTracker: [],
    priorityHeatmap: {},
  },
  analyticsPagination: {
    supervisorPerformance: [],
    paidResolutionTracker: [],
  },

  ticketSystemIssueTypeData: null,
  ticketSystemIssueTypeError: null,
  ticketSystemIssueTypeLoading: false,

  ticketSystemCategoryData: null,
  ticketSystemCategoryError: null,
  ticketSystemCategoryLoading: false,

  ticketSystemSubCategoryData: null,
  ticketSystemSubCategoryError: null,
  ticketSystemSubCategoryLoading: false,
};

export const fetchTicketsData = createAsyncThunk(
  "api/fetchTicketsData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchTickets(filters, token);
    return response;
  }
);

export const fetchTicketStatsData = createAsyncThunk(
  "api/fetchTicketStatsData",
  async (payload, { getState }) => {
    const { stats_type, token, filters = {} } = payload;
    const response = await fetchTicketStats(stats_type, token, filters);
    return response;
  }
);

// Analytics API calls
export const fetchGymwiseStats = createAsyncThunk(
  "ticketSystem/fetchGymwiseStats",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/gymwise-ticket?${queryString}`
        : `/ticketSystem/gymwise-ticket`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data;
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch gymwise stats"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchCategoryDistribution = createAsyncThunk(
  "ticketSystem/fetchCategoryDistribution",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/category-distribution-stats?${queryString}`
        : `/ticketSystem/category-distribution-stats`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data;
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch category distribution"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchInfraSpend = createAsyncThunk(
  "ticketSystem/fetchInfraSpend",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/gymwise-infra-spend?${queryString}`
        : `/ticketSystem/gymwise-infra-spend`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload;
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch infra spend"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchTatTrend = createAsyncThunk(
  "ticketSystem/fetchTatTrend",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/average-tat-trend?${queryString}`
        : `/ticketSystem/average-tat-trend`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data;
      }
      throw new Error(result?.payload?.message || "Failed to fetch TAT trend");
    } catch (error) {
      throw error;
    }
  }
);

export const fetchSupervisorPerformance = createAsyncThunk(
  "ticketSystem/fetchSupervisorPerformance",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/supervisor-performance_heatmap?${queryString}`
        : `/ticketSystem/supervisor-performance_heatmap`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return {
          data: result.payload.data || [],
          pagination: result.payload.pagination || [],
        };
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch supervisor performance"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPaidResolutionTracker = createAsyncThunk(
  "ticketSystem/fetchPaidResolutionTracker",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/paid-resolution-tracker?${queryString}`
        : `/ticketSystem/paid-resolution-tracker`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return {
          data: result.payload.data || [],
          pagination: result.payload.pagination || [],
        };
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch paid resolution tracker"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPriorityHeatmap = createAsyncThunk(
  "ticketSystem/fetchPriorityHeatmap",
  async ({ token, filters = {} }, { dispatch }) => {
    try {
      // Build query params from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/ticketSystem/issue-type-priority-heatmap?${queryString}`
        : `/ticketSystem/issue-type-priority-heatmap`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data || {};
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch priority heatmap"
      );
    } catch (error) {
      throw error;
    }
  }
);

// Add supervisor fetch thunk
export const fetchSupervisors = createAsyncThunk(
  "ticketSystem/fetchSupervisors",
  async (token, { dispatch }) => {
    try {
      const result = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/role-mapping?role_type=supervisor`,
          token,
        })
      );

      if (result?.payload?.status) {
        const supervisors = result?.payload?.data || [];
        // Transform data to match select options format
        return supervisors?.map((supervisor) => ({
          label: supervisor?.name,
          value: supervisor?.user_id,
        }));
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch supervisors"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchTicketSystemIssueTypeData = createAsyncThunk(
  "ticketSystem/fetchTicketSystemIssueTypeData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchTicketSystemIssueType(filters, token);
    return response;
  }
);

export const fetchTicketSystemCategoryData = createAsyncThunk(
  "ticketSystem/fetchTicketSystemCategoryData",
  async ({ issue_type_id, token, filters = {} }, { dispatch }) => {
    try {
      const queryParams = new URLSearchParams({
        limit: 100,
        page: 1,
        issue_type_id,
        ...filters,
      });

      const endpoint = `/ticketSystem/category?${queryParams.toString()}`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data || [];
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch categories"
      );
    } catch (error) {
      throw error;
    }
  }
);

export const fetchTicketSystemSubCategoryData = createAsyncThunk(
  "ticketSystem/fetchTicketSystemSubCategoryData",
  async ({ category_id, token, filters = {} }, { dispatch }) => {
    try {
      const queryParams = new URLSearchParams({
        limit: 100,
        page: 1,
        category_id,
        ...filters,
      });

      const endpoint = `/ticketSystem/sub-category?${queryParams.toString()}`;

      const result = await dispatch(
        fetchApiData({
          endpoint,
          token,
        })
      );

      if (result?.payload?.status) {
        return result.payload.data || [];
      }
      throw new Error(
        result?.payload?.message || "Failed to fetch sub-categories"
      );
    } catch (error) {
      throw error;
    }
  }
);

const ticketSystemSlice = createSlice({
  name: "ticketSystem",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Tickets data loading states
    builder
      .addCase(fetchTicketsData.pending, (state) => {
        state.ticketsLoading = true;
        state.ticketsError = null;
      })
      .addCase(fetchTicketsData.fulfilled, (state, action) => {
        state.ticketsLoading = false;
        state.ticketsData = action.payload.data || [];
        state.ticketsPagination = action.payload.pagination || [];
      })
      .addCase(fetchTicketsData.rejected, (state, action) => {
        state.ticketsLoading = false;
        state.ticketsError = action.error.message;
      })

      .addCase(fetchTicketStatsData.pending, (state) => {
        state.ticketStatsLoading = true;
        state.ticketStatsError = null;
      })
      .addCase(fetchTicketStatsData.fulfilled, (state, action) => {
        state.ticketStatsLoading = false;
        state.ticketStats = action.payload.data;
      })
      .addCase(fetchTicketStatsData.rejected, (state, action) => {
        state.ticketStatsLoading = false;
        state.ticketStatsError = action.error.message;
      })
      // Analytics loading states
      // Gymwise stats
      .addCase(fetchGymwiseStats.pending, (state) => {
        state.analyticsLoading.gymwise = true;
        state.analyticsError.gymwise = null;
      })
      .addCase(fetchGymwiseStats.fulfilled, (state, action) => {
        state.analyticsLoading.gymwise = false;
        state.analyticsData.gymwiseStats = action.payload;
      })
      .addCase(fetchGymwiseStats.rejected, (state, action) => {
        state.analyticsLoading.gymwise = false;
        state.analyticsError.gymwise = action.error.message;
      })
      // Category distribution
      .addCase(fetchCategoryDistribution.pending, (state) => {
        state.analyticsLoading.category = true;
        state.analyticsError.category = null;
      })
      .addCase(fetchCategoryDistribution.fulfilled, (state, action) => {
        state.analyticsLoading.category = false;
        state.analyticsData.categoryDistribution = action.payload;
      })
      .addCase(fetchCategoryDistribution.rejected, (state, action) => {
        state.analyticsLoading.category = false;
        state.analyticsError.category = action.error.message;
      })
      // Infra spend
      .addCase(fetchInfraSpend.pending, (state) => {
        state.analyticsLoading.infra = true;
        state.analyticsError.infra = null;
      })
      .addCase(fetchInfraSpend.fulfilled, (state, action) => {
        state.analyticsLoading.infra = false;
        state.analyticsData.infraSpend = action.payload;
      })
      .addCase(fetchInfraSpend.rejected, (state, action) => {
        state.analyticsLoading.infra = false;
        state.analyticsError.infra = action.error.message;
      })
      // TAT trend
      .addCase(fetchTatTrend.pending, (state) => {
        state.analyticsLoading.tatTrend = true;
        state.analyticsError.tatTrend = null;
      })
      .addCase(fetchTatTrend.fulfilled, (state, action) => {
        state.analyticsLoading.tatTrend = false;
        state.analyticsData.tatTrend = action.payload;
      })
      .addCase(fetchTatTrend.rejected, (state, action) => {
        state.analyticsLoading.tatTrend = false;
        state.analyticsError.tatTrend = action.error.message;
      })
      // Supervisor performance
      .addCase(fetchSupervisorPerformance.pending, (state) => {
        state.analyticsLoading.supervisorPerformance = true;
        state.analyticsError.supervisorPerformance = null;
      })
      .addCase(fetchSupervisorPerformance.fulfilled, (state, action) => {
        state.analyticsLoading.supervisorPerformance = false;
        state.analyticsData.supervisorPerformance = action.payload.data;
        state.analyticsPagination.supervisorPerformance =
          action.payload.pagination;
      })
      .addCase(fetchSupervisorPerformance.rejected, (state, action) => {
        state.analyticsLoading.supervisorPerformance = false;
        state.analyticsError.supervisorPerformance = action.error.message;
      })
      // Paid resolution tracker
      .addCase(fetchPaidResolutionTracker.pending, (state) => {
        state.analyticsLoading.paidResolutionTracker = true;
        state.analyticsError.paidResolutionTracker = null;
      })
      .addCase(fetchPaidResolutionTracker.fulfilled, (state, action) => {
        state.analyticsLoading.paidResolutionTracker = false;
        state.analyticsData.paidResolutionTracker = action.payload.data;
        state.analyticsPagination.paidResolutionTracker =
          action.payload.pagination;
      })
      .addCase(fetchPaidResolutionTracker.rejected, (state, action) => {
        state.analyticsLoading.paidResolutionTracker = false;
        state.analyticsError.paidResolutionTracker = action.error.message;
      })

      // Priority heatmap
      .addCase(fetchPriorityHeatmap.pending, (state) => {
        state.analyticsLoading.priorityHeatmap = true;
        state.analyticsError.priorityHeatmap = null;
      })
      .addCase(fetchPriorityHeatmap.fulfilled, (state, action) => {
        state.analyticsLoading.priorityHeatmap = false;
        state.analyticsData.priorityHeatmap = action.payload;
      })
      .addCase(fetchPriorityHeatmap.rejected, (state, action) => {
        state.analyticsLoading.priorityHeatmap = false;
        state.analyticsError.priorityHeatmap = action.error.message;
      })
      // Add supervisor reducers
      .addCase(fetchSupervisors.pending, (state) => {
        state.supervisorsLoading = true;
        state.supervisorsError = null;
      })
      .addCase(fetchSupervisors.fulfilled, (state, action) => {
        state.supervisorsLoading = false;
        state.supervisors = action.payload;
      })
      .addCase(fetchSupervisors.rejected, (state, action) => {
        state.supervisorsLoading = false;
        state.supervisorsError = action.error.message;
      })

      .addCase(fetchTicketSystemIssueTypeData.pending, (state) => {
        state.ticketSystemIssueTypeLoading = true;
        state.ticketSystemIssueTypeError = null;
      })
      .addCase(fetchTicketSystemIssueTypeData.fulfilled, (state, action) => {
        state.ticketSystemIssueTypeLoading = false;
        state.ticketSystemIssueTypeData = action.payload;
      })
      .addCase(fetchTicketSystemIssueTypeData.rejected, (state, action) => {
        state.ticketSystemIssueTypeLoading = false;
        state.ticketSystemIssueTypeError = action.error.message;
      })

      .addCase(fetchTicketSystemCategoryData.pending, (state) => {
        state.ticketSystemCategoryLoading = true;
        state.ticketSystemCategoryError = null;
      })
      .addCase(fetchTicketSystemCategoryData.fulfilled, (state, action) => {
        state.ticketSystemCategoryLoading = false;
        state.ticketSystemCategoryData = action.payload;
      })
      .addCase(fetchTicketSystemCategoryData.rejected, (state, action) => {
        state.ticketSystemCategoryLoading = false;
        state.ticketSystemCategoryError = action.error.message;
      })

      .addCase(fetchTicketSystemSubCategoryData.pending, (state) => {
        state.ticketSystemSubCategoryLoading = true;
        state.ticketSystemSubCategoryError = null;
      })
      .addCase(fetchTicketSystemSubCategoryData.fulfilled, (state, action) => {
        state.ticketSystemSubCategoryLoading = false;
        state.ticketSystemSubCategoryData = action.payload;
      })
      .addCase(fetchTicketSystemSubCategoryData.rejected, (state, action) => {
        state.ticketSystemSubCategoryLoading = false;
        state.ticketSystemSubCategoryError = action.error.message;
      });
  },
});

export default ticketSystemSlice.reducer;
